import { Box, Container, Link, Typography } from '@mui/material';
import GalleryGrid from 'components/GalleryGrid';
import IllustrationThumb from 'components/IllustrationThumb';
import GalleryHeroContainer from 'containers/gallery/GalleryHeroContainer';
import useIsMobile from 'hooks/useIsMobile';
import BaseLayout from 'layouts/BaseLayout';
import { ShowMoreContainer } from 'layouts/GalleryLayout/styles';
import renderLoaders from 'utils/renderLoaders';
import Head from 'next/head';
import { useRouter } from 'next/router';
import IllustrationsService from 'services/IllustrationsService';
import mapIllustrationProps from 'utils/mapIllustrationProps';
import CustomButton from 'components/CustomButton';
import { COLOR_GRAY_00, COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import { useEffect, useState } from 'react';
import IconsService from 'services/IconsService';
import useScrollOffset from 'hooks/useScrollOffset';
import SearchEmptyState from 'components/SearchEmptyState';
import useHasMounted from 'hooks/useHasMounted';

export default function SearchPage() {
  const router = useRouter();
  const { search } = router.query;
  const { locale } = router;
  const isMobile = useIsMobile();
  const hasMounted = useHasMounted();
  const [isIllustrationsLoading, setIsIllustrationsLoading] = useState(false);
  const [illustrationsResults, setIllustrationsResults] = useState([]);
  const relativeTags = illustrationsResults[0];
  const [currentResults, setCurrentResults] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollOffset = useScrollOffset();
  const [tagDescription, setTagDescription] = useState('');
  const [start, setStart] = useState(0);

  async function searchIllustrations(search, isFirst = true) {
    if (isFirst) {
      setIsIllustrationsLoading(true);
      setStart(0); // Only reset start on first search
    }

    try {
      const {
        data: { images, total },
      } = await IllustrationsService.search(search, locale, {
        limit: 64,
        start: isFirst ? 0 : start,
      });
      const mapped = images.map(mapIllustrationProps);

      if (isFirst) {
        setIllustrationsResults([...mapped]);
        setCurrentResults(mapped.length);
        setTotalResults(total);
      } else {
        setIllustrationsResults([...illustrationsResults, ...mapped]);
        setCurrentResults(illustrationsResults.length + mapped.length);
      }

      setTimeout(() => {
        if (start > 0) {
          window.scroll(0, scrollOffset);
        }
      }, 100);
    } catch (error) {
      if (error.response) {
        console.error(error.response.data.message);
        return;
      }

      console.error(error.message);
    } finally {
      setIsIllustrationsLoading(false);
      setIsLoadingMore(false);
    }
  }

  async function updateTagDescription() {
    const tag = await IconsService.findOneTagByName(search);

    setTagDescription(tag.data?.description);
  }

  useEffect(() => {
    if (search) {
      searchIllustrations(search, true);
    }
  }, [search]);

  useEffect(() => {
    updateTagDescription();
  }, [search]);

  useEffect(() => {
    if (start !== 0) {
      searchIllustrations(search, false);
    }
  }, [start]);

  return (
    <BaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
    >
      <Head>
        <title>{`Scientific Illustrations of ${search} | Mind the Graph`}</title>

        <meta
          key="og:title"
          name="og:title"
          property="og:title"
          content={`Scientific Illustrations of ${search} | Mind the Graph`}
        />
        <meta
          name="description"
          content={`Discover dynamic ${search} illustrations at Mind the Graph. Ideal for research and teaching projects. Explore now!`}
        />
        <meta
          key="og:description"
          name="og:description"
          property="og:description"
          content={`Discover dynamic ${search} illustrations at Mind the Graph. Ideal for research and teaching projects. Explore now!`}
        />
      </Head>

      <Box bgcolor={COLOR_GRAY_00}>
        <Container>
          <GalleryHeroContainer />
        </Container>
      </Box>

      {!isIllustrationsLoading &&
        illustrationsResults &&
        illustrationsResults.length === 0 &&
        search &&
        hasMounted && (
          <SearchEmptyState
            title="Ooops, no results were found"
            text="Don't worry! You can try searching with different keywords, see our complete list of illustrations or request a new illustration."
            buttonText="See all illustrations"
            onButtonClick={() => router.push(`/gallery`)}
            imageSrc="/images/emptystate.svg"
            imageAlt="Empty State"
          />
        )}

      {isIllustrationsLoading && <Container>{renderLoaders()}</Container>}

      <Container>
        <Box pt={4}>
          <Box pb={4}>
            {!isIllustrationsLoading && (
              <Typography component="h1" variant="h5">
                <b>{`Results for "${search}"`}</b>
              </Typography>
            )}
          </Box>

          {tagDescription && !isIllustrationsLoading && (
            <Box pb={6}>
              <Typography>{tagDescription}</Typography>
            </Box>
          )}

          {relativeTags && !isIllustrationsLoading && (
            <Box display="flex" flexWrap="wrap" whiteSpace="nowrap" pb={6}>
              <b>See also:</b>
              {relativeTags.tags.map((tag, index) => (
                <Box key={index} display="flex">
                  {tag !== search && (
                    <Link
                      href={`/illustrations/search/${tag}`}
                      style={{ color: '#000', textDecoration: 'underline' }}
                      underline="hover"
                    >
                      <Box mx={0.4}>{tag},</Box>
                    </Link>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {!isIllustrationsLoading && (
            <GalleryGrid>
              {illustrationsResults.map((illustra, index) => (
                <IllustrationThumb key={index} {...illustra} />
              ))}
            </GalleryGrid>
          )}

          {currentResults < totalResults ? (
            <ShowMoreContainer>
              <Box py={8} textAlign="center">
                {!isIllustrationsLoading && (
                  <CustomButton
                    variant="outlined"
                    isLoading={isLoadingMore}
                    onClick={() => setStart(illustrationsResults.length)}
                  >
                    Show More
                  </CustomButton>
                )}
              </Box>
            </ShowMoreContainer>
          ) : null}
        </Box>
      </Container>
    </BaseLayout>
  );
}
